import {Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {
  getFriendlyEndiannessString,
  getFriendlyTypeString,
  getReadOnlyFunctionsForDataType,
  getReadWriteFunctionsForDataType,
  is1BitDataType,
  makeFriendlyFunctionsString,
  ModbusPointID,
  ModbusPoint,
  ModbusDataType,
  ModbusEndianness,
  ModbusFunctionId,
  correctEndiannessForDataType,
  getValidEndiannessForDataType,
} from '../../../../shared/models/modbus';
import {OptionGroup, OptionGroupMultiSelect} from '../../../../shared/models/option';
import {ShadeToolsService} from '../../../../core/services/shade-tools.service';
import {ConnectedPosition} from '@angular/cdk/overlay';
import {DevicesService} from '../../../../core/services/devices.service';
import {Device, DeviceId} from '../../../../shared/models/device';
import {DrawerHeightFromBottomComponent} from '../../drawer-height-from-bottom/drawer-height-from-bottom.component';
import {FormsModule} from '@angular/forms';
import {PopoverMultiSelectComponent} from '../../popover-multi-select/popover-multi-select.component';
import {PillComponent} from '../../pill/pill.component';
import {PopoverMenuComponent} from '../../popover-menu/popover-menu.component';
import {NgClass} from '@angular/common';
import {SpinnerComponent} from '../../spinner/spinner.component';

@Component({
  selector: 'motn-modbus-single-point',
  templateUrl: './modbus-single-point.component.html',
  styleUrls: ['./modbus-single-point.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DrawerHeightFromBottomComponent,
    FormsModule,
    PopoverMultiSelectComponent,
    PopoverMenuComponent,
    NgClass,
    SpinnerComponent,
    PillComponent,
  ],
})
export class ModbusSinglePointComponent implements OnInit {
  @Input() deviceId: DeviceId;
  @Input() point: ModbusPoint;
  @Input() testingRead: boolean = false;

  name: string;
  config: ModbusPointID;
  originalConfig: ModbusPointID;
  saving: boolean = false;

  @Output() clickedOutside$: EventEmitter<null> = new EventEmitter<null>();
  @Output() saved$: EventEmitter<ModbusPoint> = new EventEmitter<ModbusPoint>();
  @Output() testRead$: EventEmitter<ModbusPointID> = new EventEmitter<ModbusPointID>();
  @Output() testWrite$: EventEmitter<ModbusPointID> = new EventEmitter<ModbusPointID>();
  @Output() testWriteFalse$: EventEmitter<ModbusPointID> = new EventEmitter<ModbusPointID>();
  @Output() testWriteTrue$: EventEmitter<ModbusPointID> = new EventEmitter<ModbusPointID>();

  overlayPositions: ConnectedPosition[] = [{
    originX: 'center',
    originY: 'top',
    overlayX: 'center',
    overlayY: 'top',
  }];


  get displayDataType(): string {
    return this.config.getFriendlyTypeString();
  }


  get displayByteOrder(): string {
    return this.config.getFriendlyEndiannessString();
  }


  get displayFunctions(): string {
    return this.config.getFriendlyFunctionsString();
  }


  dataTypeOptions: OptionGroupMultiSelect;
  functionsOptions: OptionGroup;
  byteOrderOptions: OptionGroup;


  constructor(
    private devicesService: DevicesService,
    private shadeTools: ShadeToolsService,
  ) {
  }


  ngOnInit(): void {
    if (this.point.ID === '') {
      this.point.ID = new ModbusPointID().serialize();
    } else {
      this.originalConfig = new ModbusPointID(this.point.ID);
    }

    this.name = this.point.Name;
    this.config = new ModbusPointID(this.point.ID);

    this.initializeDataTypeOptions();
    this.initializeFunctionsOptions();
    this.updateByteOrderOptions();
  }


  save() {
    if (this.saving) {
      return;
    }

    this.saving = true;

    let idChanged: boolean = false;
    const originalId = this.originalConfig?.serialize();
    const saveId = this.config.serialize();

    if (saveId !== originalId) {
      idChanged = !!this.originalConfig;

      const device: Device = Object.values(this.devicesService.sessUserDevices).find(d => d.ID === this.deviceId);

      for (let point of device.Config?.Modbus?.Points) {
        if (point.ID === saveId) {
          this.shadeTools.errorDialog('A point with the same properties (address, data type, functions, and byte order) already exists on this device.');
          this.saving = false;
          return;
        }
      }
    }

    const point: ModbusPoint = {
      ID: saveId,
      Name: this.name,
    };

    this.devicesService.saveModbusPoint$(point, this.deviceId).subscribe(
      () => {
        if (idChanged) {
          this.devicesService.deleteModbusPoint$(this.originalConfig, this.deviceId).subscribe(
            () => {
              this.afterSave(point);
            },
            () => {
              this.shadeTools.errorDialog();
              this.saving = false;
            },
          );
        } else {
          this.afterSave(point);
        }
      },
      () => {
        this.shadeTools.errorDialog();
        this.saving = false;
      },
    );
  }


  /**
   * Fetch devices here so that devices are updated in the Modbus point editor
   * component before the single point editor disappears.
   *
   * @param point
   * @private
   */
  private afterSave(point: ModbusPoint) {
    this.devicesService.fetchUserDevices$().subscribe(
      () => {
        this.saved$.emit(point);
      },
      () => {
        this.saved$.emit(point);
      },
    );
  }


  /**
   * Updates all three dropdown selections to reflect the current config state.
   */
  updateAllSelections() {
    this.updateDataTypeSelection();
    this.updateFunctionsSelection();
    this.updateByteOrderOptions();
  }


  /**
   * Called after user selects a new byte order.
   * Updates dropdown to reflect the new selection.
   */
  afterByteOrderChange() {
    this.byteOrderOptions.changeSelected(this.config.Endianness);
  }


  /**
   * Initializes the data type dropdown options once during component setup.
   * These 18 options are static and never change, only the selection is updated.
   */
  private initializeDataTypeOptions() {
    // Helper to create an option that sets both Type and Writable
    const createOption = (
      dataType: ModbusDataType,
      writable: boolean
    ) => ({
      id: `${dataType}-${writable}`,
      name: getFriendlyTypeString(dataType, writable),
      callback: () => {
        this.config.Type = dataType;
        this.config.Writable = writable;
        // Only reset endianness if current value is invalid for the new data type
        this.config.Endianness = correctEndiannessForDataType(dataType, this.config.Endianness);
      },
    });

    // Always show all 18 combinations, ordered by bittage: 1-bit → 16-bit → 32-bit → 64-bit
    // For each data type, show read-only (Input Register/Contact) first, then writable (Holding Register/Output Coil)
    const options = [
      // 1-bit Bool types
      createOption(ModbusDataType.Bool, false),  // Input Contact
      createOption(ModbusDataType.Bool, true),   // Output Coil

      // 16-bit types
      createOption(ModbusDataType.Int16, false),   // Input Register
      createOption(ModbusDataType.Int16, true),    // Holding Register
      createOption(ModbusDataType.Uint16, false),  // Input Register
      createOption(ModbusDataType.Uint16, true),   // Holding Register

      // 32-bit types
      createOption(ModbusDataType.Int32, false),   // Input Register
      createOption(ModbusDataType.Int32, true),    // Holding Register
      createOption(ModbusDataType.Uint32, false),  // Input Register
      createOption(ModbusDataType.Uint32, true),   // Holding Register
      createOption(ModbusDataType.Float32, false), // Input Register
      createOption(ModbusDataType.Float32, true),  // Holding Register

      // 64-bit types
      createOption(ModbusDataType.Int64, false),   // Input Register
      createOption(ModbusDataType.Int64, true),    // Holding Register
      createOption(ModbusDataType.Uint64, false),  // Input Register
      createOption(ModbusDataType.Uint64, true),   // Holding Register
      createOption(ModbusDataType.Float64, false), // Input Register
      createOption(ModbusDataType.Float64, true),  // Holding Register
    ];

    this.dataTypeOptions = new OptionGroupMultiSelect(options);
    this.updateDataTypeSelection();
  }


  /**
   * Updates the data type dropdown selection based on current Type and Writable values.
   */
  private updateDataTypeSelection() {
    const currentOptionId = `${this.config.Type}-${this.config.Writable}`;
    this.dataTypeOptions.changeSelected(currentOptionId);
  }


  private updateByteOrderOptions() {
    // Correct endianness to be valid for the current data type before showing options
    this.config.Endianness = correctEndiannessForDataType(this.config.Type, this.config.Endianness);

    const validEndianness = getValidEndiannessForDataType(this.config.Type);

    // Only show valid endianness options (filtered list)
    const options = validEndianness.map((endianness) => ({
      id: endianness,
      name: getFriendlyEndiannessString(endianness),
      callback: () => {
        this.config.Endianness = endianness;
      },
    }));

    this.byteOrderOptions = new OptionGroup(options);

    // Mark the currently selected option
    this.byteOrderOptions.changeSelected(this.config.Endianness);
  }


  /**
   * Initializes the functions dropdown options once during component setup.
   * Creates the 4 Modbus function options based on the protocol specification.
   */
  private initializeFunctionsOptions() {
    const options = [
      // Bool functions - Discrete Inputs (read-only)
      {
        id: ModbusFunctionId.READ_DISCRETE_INPUTS,
        name: 'Read (2)',
        callback: () => {
          this.config.Type = ModbusDataType.Bool;
          this.config.Writable = false;
          this.config.Endianness = ModbusEndianness.Empty;
        },
      },
      // Bool functions - Coils (writable)
      {
        id: ModbusFunctionId.READ_WRITE_COILS,
        name: 'Read (1) / Write (5)',
        callback: () => {
          this.config.Type = ModbusDataType.Bool;
          this.config.Writable = true;
          this.config.Endianness = ModbusEndianness.Empty;
        },
      },
      // Register functions - Input Registers (read-only)
      {
        id: ModbusFunctionId.READ_INPUT_REGISTERS,
        name: 'Read (4)',
        callback: () => {
          this.autoCorrectBoolToInt16();
          this.config.Writable = false;
        },
      },
      // Register functions - Holding Registers (writable)
      {
        id: ModbusFunctionId.READ_WRITE_HOLDING,
        name: 'Read (3) / Write (6)',
        callback: () => {
          this.autoCorrectBoolToInt16();
          this.config.Writable = true;
        },
      },
    ];

    this.functionsOptions = new OptionGroup(options);
    this.updateFunctionsSelection();
  }


  /**
   * Auto-corrects Bool data type to Int16 with BigEndian when switching to register functions.
   * Bool types cannot use Input/Holding Register functions.
   */
  private autoCorrectBoolToInt16() {
    if (this.config.Type === ModbusDataType.Bool) {
      this.config.Type = ModbusDataType.Int16;
      this.config.Endianness = ModbusEndianness.BigEndian;
    }
  }


  /**
   * Updates the functions dropdown selection based on the config's Type and Writable properties.
   */
  private updateFunctionsSelection() {
    const currentFunctionId = this.config.Type === ModbusDataType.Bool
      ? (this.config.Writable ? ModbusFunctionId.READ_WRITE_COILS : ModbusFunctionId.READ_DISCRETE_INPUTS)
      : (this.config.Writable ? ModbusFunctionId.READ_WRITE_HOLDING : ModbusFunctionId.READ_INPUT_REGISTERS);

    this.functionsOptions.changeSelected(currentFunctionId);
  }


  /**
   * Checks if the current config is a 1-bit data type.
   * Used in the template to show boolean write buttons.
   */
  is1BitType(): boolean {
    return is1BitDataType(this.config.Type);
  }


}
